import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';

export const STEP_TIMELINE_CONFIG = {
  LIGHT_TRAVEL_SECONDS: 44,
  NODE_PULSE_MS: 600,
  NODE_SIZE_PX: 33.6,
  DOT_SIZE_PX: 10.8,
  GLOW_BLUR_PX: 14.4,
  LIGHT_FADE_MS: 200,
  BREAKPOINT_VERTICAL_PX: 1100,
  FLOW_DOT_COUNT: 64,
};

interface StepTimelineProps {
  count?: number;
  orientation?: 'auto' | 'horizontal' | 'vertical';
  className?: string;
  cardsRef?: React.RefObject<HTMLOListElement | null>;
}

const StepTimeline: React.FC<StepTimelineProps> = ({
  count = 4, orientation = 'auto', className, cardsRef,
}) => {
  if (!Number.isInteger(count) || count < 1) {
    throw new RangeError('StepTimeline count must be a positive integer.');
  }
  const narrow = useMediaQuery(`(max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`);
  const vertical = orientation === 'vertical' || (orientation === 'auto' && narrow);
  const rootRef = useRef<HTMLDivElement>(null);
  const [centers, setCenters] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const { LIGHT_TRAVEL_SECONDS: travel, NODE_PULSE_MS, NODE_SIZE_PX: size,
    LIGHT_FADE_MS, FLOW_DOT_COUNT } = STEP_TIMELINE_CONFIG;
  const pulse = NODE_PULSE_MS / 1000;
  const fade = LIGHT_FADE_MS / 1000;
  const cycle = travel + Math.max(pulse, fade) + fade;
  const percent = (seconds: number) => `${seconds / cycle * 100}%`;

  useLayoutEffect(() => {
    const root = rootRef.current;
    const cards = cardsRef?.current;
    if (!root || !cards) { setCenters([]); return; }
    const measure = () => {
      const bounds = root.getBoundingClientRect();
      const next = Array.from(cards.children).slice(0, count).map((card) => {
        const rect = card.getBoundingClientRect();
        return vertical ? rect.top + rect.height / 2 - bounds.top
          : rect.left + rect.width / 2 - bounds.left;
      });
      setCenters((previous) => previous.length === next.length
        && previous.every((value, index) => Math.abs(value - next[index]) < 0.1) ? previous : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    [root, cards, ...Array.from(cards.children)].forEach((element) => observer.observe(element));
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [cardsRef, count, vertical]);

  useEffect(() => {
    let inView = false;
    const sync = () => setRunning(inView && document.visibilityState !== 'hidden');
    const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; sync(); });
    if (rootRef.current) observer.observe(rootRef.current);
    document.addEventListener('visibilitychange', sync);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', sync); };
  }, []);

  const positions = Array.from({ length: count }, (_, index) =>
    centers.length === count ? `${centers[index]}px` : `${(index + 0.5) / count * 100}%`);
  const start = positions[0];
  const end = positions[count - 1];
  const animation = (name: string) => `${name} ${cycle}s linear infinite`;
  const travelAnimation = vertical ? 'stepTimelineTravelVertical' : 'stepTimelineTravelHorizontal';

  return (
    <Box ref={rootRef} className={className} aria-hidden="true" data-step-timeline
      data-orientation={vertical ? 'vertical' : 'horizontal'} data-running={running}
      sx={{
        position: vertical && cardsRef ? 'absolute' : 'relative',
        ...(vertical ? { left: 0, top: 0, bottom: 0, width: size, ...(!cardsRef ? { height: count * 100 } : {}) }
          : { width: '100%', height: size, mb: 3, order: -1 }),
        pointerEvents: 'none', color: C.millosMint,
        '& [data-timeline-animated]': { animationPlayState: 'paused' },
        '&[data-running="true"] [data-timeline-animated]': { animationPlayState: 'running' },
        '@media (prefers-reduced-motion: reduce)': {
          '& [data-timeline-animated]': { animation: 'none', opacity: 0 },
          '& [data-timeline-flow-mover]': { opacity: 1 },
          '& [data-timeline-flow-dot]': { opacity: 0.4 },
        },
        [`@keyframes ${travelAnimation}`]: {
          '0%': { transform: 'translate(0, 0)' },
          [`${percent(travel)}, 100%`]: { transform: vertical ? 'translateY(100%)' : 'translateX(100%)' },
        },
        '@keyframes stepTimelineFade': {
          '0%, 100%': { opacity: 0 },
          [`${percent(fade)}, ${percent(travel)}`]: { opacity: 1 },
          [percent(travel + fade)]: { opacity: 0 },
        },
        '@keyframes stepTimelinePulse': {
          '0%': { transform: 'scale(1)', opacity: 0.4 },
          [`${percent(pulse)}, 100%`]: { transform: 'scale(1.6)', opacity: 0 },
        },
      }}>
      <Box data-timeline-track sx={{
        position: 'absolute',
        ...(vertical ? { top: start, height: `calc(${end} - ${start})`, left: '50%', width: '1px' }
          : { left: start, width: `calc(${end} - ${start})`, top: '50%', height: '1px' }),
      }}>
        {count > 1 && Array.from({ length: FLOW_DOT_COUNT }, (_, index) => <Box key={index} data-timeline-animated data-timeline-flow-mover sx={{
          position: 'absolute', inset: 0, animation: animation(travelAnimation), animationDelay: `${-index / FLOW_DOT_COUNT * cycle}s`,
          '@media (prefers-reduced-motion: reduce)': {
            transform: vertical ? `translateY(${index / FLOW_DOT_COUNT * 100}%)` : `translateX(${index / FLOW_DOT_COUNT * 100}%)`,
          },
        }}>
          <Box data-timeline-animated data-timeline-flow-dot sx={{
            position: 'absolute',
            left: vertical ? (index * 7 % 11) - 5 : -1.5,
            top: vertical ? -1.5 : (index * 7 % 11) - 5,
            width: index % 3 === 0 ? 4 : 2.5, height: index % 3 === 0 ? 4 : 2.5,
            borderRadius: '50%', bgcolor: [V.brandCyan, V.brandBlue, V.brandTeal, V.brandViolet][index % 4],
            boxShadow: `0 0 4px ${V.paperAlpha18}`,
            animation: animation('stepTimelineFade'), animationDelay: `${-index / FLOW_DOT_COUNT * cycle}s`,
          }} />
        </Box>)}
      </Box>
      {positions.map((position, index) => (
        <Box key={index} data-timeline-node sx={{
          position: 'absolute', zIndex: 1, width: size, height: size,
          ...(vertical ? { top: position, left: '50%' } : { left: position, top: '50%' }),
          transform: 'translate(-50%, -50%)',
          display: 'grid', placeItems: 'center', borderRadius: '50%',
          bgcolor: C.heroGradient, border: '1px solid currentColor',
          fontFamily: F.display, fontSize: 18.72, lineHeight: 1,
        }}>
          <Box data-timeline-animated sx={{
            '--pulse-delay': `${count === 1 ? 0 : index / (count - 1) * travel}s`,
            position: 'absolute', inset: -1, border: '1px solid currentColor', borderRadius: '50%',
            opacity: 0, animation: animation('stepTimelinePulse'), animationDelay: 'var(--pulse-delay)',
          }} />
          {index + 1}
        </Box>
      ))}
    </Box>
  );
};

export default StepTimeline;
